import React, { useState } from 'react';
import { Form } from 'antd';
import { CSVLink } from 'react-csv';
import ModalTitle from '../../../common/modalTitle/ModalTitle';
import {
    MFRProject,
    ProjectReferences,
    ProjectTableFields,
} from '../../../../models/project/types';
import { Text, CloseIcon } from '../../../../styles/common.styles';
import { blueDark01 } from '../../../../styles/colors';
import { ExportProjectModal, ExportProjectSpace, ExportSpin } from './ExportProject.styles';
import ModalFooter from '../../../common/modalFooter/ModalFooter';
import { GetProjectInformationResponse } from '../../../../redux/actions/projectActions/types';
import CustomSelect from '../../../common/select/CustomSelect';
import { SelectData } from '../../../../models/common/types';
import { useAppDispatch } from '../../../../hooks/redux';
import { Product } from '../../../../models/product/types';
import { CSVHeader } from './ExportProject.mockData';
import { ExportProjectType } from '../../../../models/project/enums';
import { Division } from '../../../../models/productDivision/types';
import { alertService } from '../../../../services';
import { exportProjectProducts } from '../../../../helpers/productExportHelper';

interface Props {
    isOpen: boolean;
    projectReferences: ProjectReferences | null;
    project: ProjectTableFields | GetProjectInformationResponse | MFRProject | null;
    options: Array<SelectData>;
    division?: Division<Product>;
    wrapLinesInDropdown?: boolean;
    // reload: (modal: string) => void;
    closeModal: (data: { key: 'csv' }) => void;
}

interface State {
    exportData: Array<any>;
    fileName: string;
    pending: boolean;
}

const ExportProject: React.FC<Props> = ({
    isOpen,
    projectReferences,
    options,
    project,
    division,
    wrapLinesInDropdown,
    closeModal,
}) => {
    const dispatch = useAppDispatch();

    const [state, setState] = useState<State>({
        exportData: [],
        fileName: 'export products',
        pending: false,
    });

    const handleCancel = () => {
        closeModal({ key: 'csv' });
    };

    const onSubmit = async (data: { exportType: string }) => {
        setState({ ...state, pending: true });

        if (!project){
             return;}

        const positiveDesignId = projectReferences?.allBasisOfDesigns.find(
            (a) => a.name === 'Yes',
        )?.id;

        await exportProjectProducts({
            dispatch,
            projectId: project.id,
            filterProducts: (products) => {
                let filtered = products;

                switch (+data.exportType) {
                    case ExportProjectType.INSTALLED_PRODUCTS_ALL_DIVISIONS_ALL_PRODUCTS:
                        filtered = filtered.filter((cp) => cp.isInstalled);
                        break;
                    case ExportProjectType.ALL_DIVISIONS_ALL_PRODUCTS:
                        break;
                    case ExportProjectType.INSTALLED_PRODUCTS_ALL_DIVISIONS_BASIS_DESIGN:
                        filtered = filtered.filter(
                            (cp) =>
                                cp.isInstalled &&
                                cp.basisOfDesignId?.toString() === positiveDesignId?.toString(),
                        );
                        break;
                    case ExportProjectType.ALL_DIVISIONS_BASIS_DESIGN:
                        filtered = filtered.filter(
                            (cp) => cp.basisOfDesignId?.toString() === positiveDesignId?.toString(),
                        );
                        break;
                    case ExportProjectType.DIVISION_ALL_PRODUCTS:
                        filtered = filtered.filter(
                            (cp) => cp.mfDivision.number?.toString() === division?.id?.toString(),
                        );
                        break;
                    case ExportProjectType.INSTALLED_PRODUCTS_DIVISION_ALL_PRODUCTS:
                        filtered = filtered.filter(
                            (cp) =>
                                cp.isInstalled &&
                                cp.mfDivision.number?.toString() === division?.id?.toString(),
                        );
                        break;
                    case ExportProjectType.DIVISION_BASIS_DESIGN:
                        filtered = filtered.filter(
                            (cp) =>
                                cp.mfDivision.number?.toString() === division?.id?.toString() &&
                                cp.basisOfDesignId?.toString() === positiveDesignId?.toString(),
                        );
                        break;
                    case ExportProjectType.INSTALLED_PRODUCTS_DIVISION_BASIS_DESIGN:
                        filtered = filtered.filter(
                            (cp) =>
                                cp.isInstalled &&
                                cp.mfDivision.number?.toString() === division?.id?.toString() &&
                                cp.basisOfDesignId?.toString() === positiveDesignId?.toString(),
                        );
                        break;
                    default:
                        break;
                }

                // Add project-specific fields to products
                return filtered.map((p) => ({
                    ...p,
                    basisOfDesign: projectReferences?.allBasisOfDesigns.find(
                        (a) => a.id.toString() === p.basisOfDesignId?.toString(),
                    )?.name,
                    substitutions: projectReferences?.allProductSubstitutions.find(
                        (s) => s.id.toString() === p.substitutionTypeId?.toString(),
                    )?.name,
                }));
            },
            generateFileName: () => {
                const sName = options.find((o) => o.id === data.exportType)?.name;
                let fileNameCalc = '';
                let divNumber = '';

                if (division) {
                    divNumber = division.id.substring(0, 2);
                    divNumber = divNumber.startsWith('0') ? divNumber.slice(1) : divNumber;
                }

                switch (sName) {
                    case 'All divisions, all products':
                        fileNameCalc = project.name + '-All Products';
                        break;
                    case 'All divisions, basis of design only':
                        fileNameCalc = project.name + '-All Products-BoD';
                        break;
                    case 'Installed products - all divisions, all products':
                        fileNameCalc = project.name + '-Installed Products-All Products';
                        break;
                    case 'Installed products - all divisions, basis of design only':
                        fileNameCalc = project.name + '-Installed Products-All Products-BoD';
                        break;
                    case division?.name + ', all products':
                        fileNameCalc = `${project.name  }-Division ${  divNumber  }-All Products`;
                        break;
                    case division?.name + ', basis of design only':
                        fileNameCalc = `${project.name  }-Division ${  divNumber  }-All Products-BoD`;
                        break;
                    case `Installed products - ${division?.name}, all products`:
                        fileNameCalc = `${project.name  }-Division ${  divNumber  }-Installed Products-All Products`;
                        break;
                    case `Installed products - ${division?.name}, basis of design only`:
                        fileNameCalc = `${project.name  }-Division ${  divNumber  }-Installed Products-All Products-BoD`;
                        break;
                    default:
                        break;
                }

                if (!fileNameCalc?.includes('.csv')) {
                    fileNameCalc += '.csv';
                }

                return fileNameCalc;
            },
            onSuccess: (currentProducts, fileNameCalc) => {
                setState({
                    ...state,
                    fileName: fileNameCalc || state.fileName,
                    exportData: currentProducts,
                    pending: false,
                });

                setTimeout(() => {
                    const link = document.getElementById('csv-link') as any;
                    link.click();
                    closeModal({ key: 'csv' });
                }, 500);
            },
            onError: (message) => {
                alertService.warn(message);
                setState({ ...state, pending: false });
            },
        });
    };

    return (
        <ExportProjectModal
            open={isOpen}
            onCancel={handleCancel}
            closeIcon={<CloseIcon />}
            centered
            footer={null}
        >
            <ExportSpin spinning={state.pending}>
                <ModalTitle title="Export to CSV" icon="ic-modal-title-export.svg" />
                <div>
                    <Text weight={400} size={15} height={20} color={blueDark01}>
                        Export to CSV includes a spreadsheet of product name, manufacturer name,
                        MasterFormat number, URL to their Transparency Catalog listing page,
                        optionally added attributes (pricing, BOD, substitutions), and the types of
                        transparency documentation for that product.
                    </Text>
                    <CSVLink
                        id="csv-link"
                        filename={state.fileName}
                        data={state.exportData}
                        headers={CSVHeader}
                    >
                        Download me
                    </CSVLink>
                </div>
                <Form onFinish={onSubmit}>
                    <ExportProjectSpace>
                        <CustomSelect
                            id="ModalSelect"
                            label="Export type:"
                            name="exportType"
                            options={options}
                            placeholder="Select one"
                            rules={[{ required: true, message: '' }]}
                            block
                            wrapLines={wrapLinesInDropdown}
                        />
                    </ExportProjectSpace>
                    <ModalFooter
                        action="Export"
                        cancel={handleCancel}
                        cancelText="Cancel"
                        submit={() => { }}
                    />
                </Form>
            </ExportSpin>
        </ExportProjectModal>
    );
};

export default ExportProject;
